import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Tooltip } from "antd";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { CSVLink } from "react-csv";
import InvestorsList from "./pages/investors-list";
import InvestorProfile from "./pages/investors-profile";
import { BeatLoader } from "react-spinners";
import { FlorencePageSkeleton } from "../../../../components/page-skeleton";
import TargetList from "./components/target-list";
import InvestorDescription from "./pages/investor-description";
import { toast } from "react-toastify";
import { InfoOutlined } from "@mui/icons-material";
import { ENTERPRISE_ROLE } from "../../../../constant";
import AiTargetLimitError from "./components/target-limit-error";
import AddLeadModal from "../../../../components/add-lead-modal";
import { resetAlgo, startAlgo, setAlgoPreview } from "../../../../store/actions/algo";
import { setProfile } from "../../../../store/actions/user";
import SubscriptionModal from "../../../../components/subscription-modal";
import FreeTrialBanner from "../../../../components/free-trial-banner";
import GeneratingPdfModal from "../../../../components/generating-pdf-modal";
import { buyerListMauplator } from "../../../../helpers/manuplator";
import { BuyerPDF, buildFlorenceIndustrySections, sortBuyersByLlmRank } from "../../../../helpers/pdfGenerator";
import {
  INVESTOR_LOGO_HOST,
  LOGO_DEV_PUBLIC_KEY,
  recordActions,
} from "../../../../constant";

const getRecommendationLabels = (companyType) => {
  if (companyType === "Acquire") {
    return {
      singular: "Target",
      plural: "Targets",
      snapshot: "Target Snapshot",
      reason: "Reasons for Target",
    };
  }

  if (companyType === "Sell") {
    return {
      singular: "Buyer",
      plural: "Buyers",
      snapshot: "Buyer Snapshot",
      reason: "Reasons for Buyer",
    };
  }

  return {
    singular: "Investor",
    plural: "Investors",
    snapshot: "Investor Snapshot",
    reason: "Reasons for Recommendation",
  };
};

/** Matches Florence xl layout / global.scss large-screen breakpoint */
const FLORENCE_INVESTORS_PAGE_BREAKPOINT = 1800;
const FLORENCE_INVESTORS_PAGE_SIZE_LARGE = 5;
const FLORENCE_INVESTORS_PAGE_SIZE_SMALL = 5;

const AIRecommendation = () => {
  // STATES
  const [state, setState] = useState({
    selectedRowKeys: [0],
    profile: {},
  });
  const [initLoad, setInitLoad] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedFilters, setselectedFilters] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [investors, setInvestors] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [generatePreview, setGeneratePreview] = useState(false);
  const [animationPreview, setAnimationPreview] = useState(false);
  const [generateLoader, setGenerateLoader] = useState(false);
  const [limitErrorPreview, setLimitErrorPreview] = useState(false);
  const [addLeadPreview, setAddLeadPreview] = useState(false);
  const [subscriptionPreview, setSubscriptionPreview] = useState(false);
  const [generatePDFPreview, setGeneratePDFPreview] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(0);
  const [isPdfLoading, setPdfLoading] = useState(false);
  const [isCsvLoading, setCsvLoading] = useState(false);
  const [selectedInvestors, setSelectedInvestors] = useState([]);
  const [preferredIndustryPreview, setPreferredIndustryPreview] = useState(false);
  const downloadCsvBtn = useRef(null);

  // CONST VALS
  const { http } = global.services;
  const dispatch = useDispatch();
  const isFlorenceLargeViewport = useMediaQuery({
    query: `(min-width: ${FLORENCE_INVESTORS_PAGE_BREAKPOINT}px)`,
  });
  const dataLimit = isFlorenceLargeViewport
    ? FLORENCE_INVESTORS_PAGE_SIZE_LARGE
    : FLORENCE_INVESTORS_PAGE_SIZE_SMALL;
const CSV_HEADERS = [
  { label: "Name", key: "name" },
  { label: "Rank (LLM)", key: "rank" },
  { label: "Tier", key: "tier" },
  { label: "Type", key: "type" },
  { label: "URL", key: "website" },
  { label: "Location", key: "location" },
  { label: "State", key: "state" },
  { label: "Country", key: "country" },
  { label: "HQ Address", key: "hq_address" },
  ...(preferredIndustryPreview ? [{ label: "Preferred Industry", key: "user_preferred_industry" }] : []),
  // { label: "Preferred Industry", key: "user_preferred_industry" },
  { label: "Tags", key: "tags" },
  { label: "Description", key: "description" },
  { label: "Specialty Interests", key: "specialty_interests" },
  { label: "Attribute Interests", key: "attribute_interests" },
  { label: "Broad Specialty", key: "broad_specialty" },
  { label: "Narrow Specialty", key: "narrow_specialty" },
  { label: "NAICS Industry", key: "naics_six_digit_industry" },
  { label: "Products", key: "products" },
  { label: "Services", key: "services" },
  { label: "Preferred EBITDA (in Millions)", key: "preferred_ebitda" },
  { label: "Preferred Revenue (in Millions)", key: "preferred_revenue" },
  { label: "Preferred EV (in Millions)", key: "preferred_ev" },
  { label: "Typical Equity Investment (in Millions)", key: "typical_equity_investments" },
  { label: "Reasons For Target", key: "reasons_for_target" },
];

  // REDUX DATA
  const selectedCompany = useSelector((state) => state.CompanyReducer.selected_company);
  const user = useSelector((state) => state.UserReducer.profile);
  const { companies, lastUpdated } = useSelector((state) => state.algoReducer);

  const isAlgoCompleted = companies[selectedCompany.id]?.completed || false;
  const isAlgoRunning = companies[selectedCompany.id]?.running || false;
  const companyType = selectedCompany?.user_input?.[0]?.["question-one"]?.[1];
  const companyPreferredIndustry =
    selectedCompany?.user_input?.[0]?.["industry_segmentation"]?.[1] ?? [];
  const recommendationLabels = getRecommendationLabels(companyType);

  // Florence access: free trial = remaining_buyers_trial (3 total); subscribed = algo_access (100/month)
  const florenceAccess = user?.free_trial
    ? (user?.remaining_buyers_trial ?? 0) > 0
    : user?.algo_access;

  const isCompanyUpdated =
    selectedCompany?.last_updated &&
    lastUpdated &&
    selectedCompany?.last_updated > lastUpdated;
  const warningPreview =
    isCompanyUpdated && !isAlgoRunning && !generatePreview && florenceAccess && !isLoading;

  const prevDataLimitRef = useRef(undefined);
  if (prevDataLimitRef.current !== dataLimit) {
    if (prevDataLimitRef.current !== undefined) {
      setPage(1);
      setState((s) => ({ ...s, selectedRowKeys: [0] }));
    }
    prevDataLimitRef.current = dataLimit;
  }

  // HELPERS
  const getInvestorsHelper = async (companyid, isAll, initialCall) => {
    const params = {
      ...selectedFilters,
      offset: isAll ? 0 : (page - 1) * dataLimit,
      search,
    };
    if (!isAll) {
      params["limit"] = dataLimit;
    }
    Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
    const queryparams = new URLSearchParams(params).toString();
    try {
      const { data } = await http.get(`company-buyers/${companyid}?${queryparams}`);
      const buyers = data?.data;
      setGeneratePreview(data?.total === 0 && (initLoad || initialCall));
      setPreferredIndustryPreview(data?.preferred_industry_preview);
      setInitLoad(false);
      setLoading(false);
      return {
        buyers,
        total: data?.total,
      };
    } catch (err) {
      toast.error(err?.response?.data?.error ?? "Something went wrong");
      setLoading(false);
    }
  };

  const init = async (fromCompany) => {
    const { id } = selectedCompany;
    if (id) {
      const { buyers, total } = await getInvestorsHelper(id, false, fromCompany);
      setTotal(total);
      let newState = { ...state };
      if (!newState.selectedRowKeys.length) {
        newState.selectedRowKeys = [0];
      }
      if (buyers && buyers.length > 0) {
        const sIndex = newState.selectedRowKeys[0];
        newState.profile = buyers[sIndex].programx_output;
      } else {
        newState.profile = {};
      }
      if (buyers) {
        setInvestors(buyers);
        setState(newState);
        setTimeout(() => {
          setAnimationPreview(false);
        }, 5000);
      }
    }
  };

  // HANDLERS
  const generateAiList = async () => {
    if (generateLoader) return;
    if (!florenceAccess) {
      setLimitErrorPreview(true);
      return;
    }
    setGenerateLoader(true);
    try {
      const { data } = await http.put(`company-buyers/${selectedCompany.id}`);
      setGenerateLoader(false);
      if (data?.remaining_buyers_trial !== undefined) {
        dispatch(
          setProfile({
            ...user,
            remaining_buyers_trial: data.remaining_buyers_trial,
          }),
        );
      }
      dispatch(startAlgo({ companyId: selectedCompany.id, algoId: data.algo }));
    } catch (err) {
      setGenerateLoader(false);
      toast.error(err?.response?.data?.error ?? "Something went wrong");
    }
  };

  const setFiltersHandler = (value) => {
    setselectedFilters(value);
  };
  const handlePageChange = (page) => {
    setPage(page);
    setState({
      ...state,
      selectedRowKeys: [0],
    });
  };
  const setSearchHandler = (val) => {
    setSearch(val);
  };

  const changeSelected = (index) => {
    setState({
      ...state,
      selectedRowKeys: index,
      profile: investors[index[0]].programx_output,
    });
  };

  const nextBuyerHandler = () => {
    const selectedIndex = state.selectedRowKeys[0];
    const nextIndex = selectedIndex + 1;
    if (nextIndex >= dataLimit) {
      if (page * dataLimit >= total) return;
      setPage(page + 1);
      setState({
        ...state,
        selectedRowKeys: [0],
      });
      return;
    }
    if (nextIndex >= investors.length) return;
    setState({
      ...state,
      selectedRowKeys: [nextIndex],
      profile: investors[nextIndex].programx_output,
    });
  };
  const prevBuyerHandler = () => {
    const selectedIndex = state.selectedRowKeys[0];
    const prevIndex = selectedIndex - 1;
    if (prevIndex < 0) {
      if (page === 1) return;
      setPage(page - 1);
      setState({
        ...state,
        selectedRowKeys: [dataLimit - 1],
      });
      return;
    }
    setState({
      ...state,
      selectedRowKeys: [prevIndex],
      profile: investors[prevIndex].programx_output,
    });
  };

  const downloadPDF = async () => {
    setGenerateLoading(0);
    setPdfLoading(true);
    const { buyers } = await getInvestorsHelper(selectedCompany?.id, true);
    setGeneratePDFPreview(true);
    const interval = setInterval(() => {
      setGenerateLoading((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 7;
      });
    }, 100);

    const processedData = await Promise.all(
      (buyers || []).map(async (item) => {
        const logoUrl =
          INVESTOR_LOGO_HOST + item.programx_output?.url + `?token=${LOGO_DEV_PUBLIC_KEY}`;
        return {
          ...item,
          logoSrc: logoUrl,
        };
      }),
    );
    const pdfSections = buildFlorenceIndustrySections(processedData, companyPreferredIndustry);
    const title = `${selectedCompany?.company_name} ${recommendationLabels.plural}`;
    const blob = await pdf(<BuyerPDF sections={pdfSections} title={title} />).toBlob();
    setPdfLoading(false);
    setGenerateLoading(100);
    clearInterval(interval);
    http.post("action-record", {
      action: recordActions.buyerPDF,
    });
    setGeneratePDFPreview(false);
    saveAs(blob, `${title}.pdf`);
  };

  const downloadCsv = () => {
    setCsvLoading(true);
    getInvestorsHelper(selectedCompany?.id, true).then(({ buyers }) => {
      setSelectedInvestors(buyerListMauplator(sortBuyersByLlmRank(buyers || [])));
      setCsvLoading(false);
      http.post("action-record", {
        action: recordActions.buyerCSV,
      });
      downloadCsvBtn?.current?.link.click();
    });
  };

  // HOOKS
  useEffect(() => {
    init();
  }, [page, dataLimit]);

  useEffect(() => {
    setPage(1);
    init(false);
  }, [search, selectedFilters]);

  // HOOKS
  useEffect(() => {
    init(true);
  }, [selectedCompany]);

  useEffect(() => {
    if (isAlgoCompleted) {
      setAnimationPreview(true);
      init();
      dispatch(resetAlgo({ companyId: selectedCompany?.id }));
    }
  }, [isAlgoCompleted]);

  return (
    <div className="florence-page">
      {warningPreview && (
        <div className="florence-company-updated-alert">
          <div className="florence-company-updated-alert__accent" />
          <div className="florence-company-updated-alert__content">
            <div className="florence-company-updated-alert__header">
              <span className="florence-company-updated-alert__icon">
                <InfoOutlined />
              </span>
              <div className="florence-company-updated-alert__text">
                <h3 className="florence-company-updated-alert__title">
                  Company details have changed
                </h3>
                <p className="florence-company-updated-alert__description">
                  We've found updates to this company's details. Regenerate the list to get
                  updated recommendations.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="top-header florence-page-actions">
        <div className="florence-page-actions-copy">
          <h2 className="page-title">Florence</h2>
          <p className="florence-section-description">
            Review {recommendationLabels.plural.toLowerCase()} and firm details first; Florence's reasoning for the selected {recommendationLabels.singular.toLowerCase()} appears below.
          </p>
        </div>
        {!generatePreview && !isLoading && (
          <div className="action-box">
            <button
              type="button"
              className="download-btn toolbar-download-btn"
              disabled={!investors?.length || isPdfLoading}
              onClick={downloadPDF}
            >
              {isPdfLoading ? <BeatLoader className="loader" color="#fff" size={7} /> : "Download PDF"}
            </button>
            <button
              type="button"
              className="download-btn toolbar-download-btn"
              disabled={!investors?.length || isCsvLoading}
              onClick={downloadCsv}
            >
              {isCsvLoading ? <BeatLoader className="loader" color="#fff" size={7} /> : "Download CSV"}
            </button>
            {!florenceAccess ? (
              <Tooltip
                title={
                  user?.free_trial ? "Free trial limit reached" : "Monthly limit reached"
                }
              >
                <span>
                  <button disabled className="regenerate-toggle disabled">
                    Regenerate List
                  </button>
                </span>
              </Tooltip>
            ) : (
              <button
                disabled={generateLoader || isAlgoRunning}
                onClick={generateAiList}
                className="regenerate-toggle"
              >
                {generateLoader ? (
                  <BeatLoader color="#fff" size={8} />
                ) : (
                  "Regenerate List"
                )}
              </button>
            )}
          </div>
        )}
      </div>
      {user?.free_trial && user?.role !== ENTERPRISE_ROLE && (
        <FreeTrialBanner
          title={
            user?.remaining_buyers_trial === 0
              ? "Your free trial for Florence has ended!"
              : "Enjoy your free trial!"
          }
          description={
            user?.remaining_buyers_trial === 0 ? (
              "You've used all 3 free Florence searches. Upgrade to unlock more."
            ) : user?.remaining_buyers_trial === 1 ? (
              "This is your last free Florence search. Upgrade to keep generating lists."
            ) : (
              <>
                You have{" "}
                <strong>{user?.remaining_buyers_trial} free Florence searches</strong>{" "}
                left.
              </>
            )
          }
          remainingCount={user?.remaining_buyers_trial ?? 0}
          maxCount={3}
          onUpgrade={() => setSubscriptionPreview(true)}
        />
      )}
      {isLoading ? (
        <FlorencePageSkeleton />
      ) : generatePreview ? (
        <TargetList
          generateHandler={generateAiList}
          generateLoader={generateLoader}
          isAlgoRunning={isAlgoRunning}
          algoAccess={florenceAccess}
          recommendationLabels={recommendationLabels}
          onViewProgress={() =>
            dispatch(
              setAlgoPreview({ companyId: selectedCompany.id, preview: true }),
            )
          }
        />
      ) : (
        <div className="florence-workspace">
          <Row gutter={[14, 10]} className="florence-main-grid" align="stretch">
            <Col xs={24} xl={15} className="florence-main-grid-left">
              <InvestorsList
                selectedCompany={selectedCompany}
                data={investors || []}
                selected={state.profile}
                state={state}
                preferredIndustryPreview={preferredIndustryPreview}
                onChange={changeSelected}
                onPrev={prevBuyerHandler}
                onNext={nextBuyerHandler}
                datalimit={dataLimit}
                total={total}
                page={page}
                pageChanger={handlePageChange}
                search={search}
                setSearch={setSearchHandler}
                selectedFilter={selectedFilters}
                setFilters={setFiltersHandler}
                getInvestors={getInvestorsHelper}
              />
              <InvestorDescription
                animationPreview={animationPreview}
                data={state.profile}
                recommendationLabels={recommendationLabels}
              />
            </Col>
            <Col xs={24} xl={9} className="florence-main-grid-right">
              <InvestorProfile
                data={state.profile}
                isFlorenceLargeViewport={isFlorenceLargeViewport}
                onAdd={() => setAddLeadPreview(true)}
                recommendationLabels={recommendationLabels}
                preferredIndustryPreview={preferredIndustryPreview}
              />
            </Col>
          </Row>
        </div>
      )}
      <AiTargetLimitError
        preview={limitErrorPreview}
        previewHandler={() => setLimitErrorPreview(!limitErrorPreview)}
      />

      <AddLeadModal
        preview={addLeadPreview}
        title={state.profile?.company_name}
        data={state.profile}
        closeHandler={() => setAddLeadPreview(false)}
      />
      <SubscriptionModal
        preview={subscriptionPreview}
        closeHandler={() => setSubscriptionPreview(false)}
      />
      <div style={{ display: "none" }}>
        <CSVLink
          data={selectedInvestors}
          ref={downloadCsvBtn}
          filename={`${selectedCompany?.company_name?.replaceAll(".", " ")} ${recommendationLabels.plural}`}
          headers={CSV_HEADERS}
        />
      </div>
      <GeneratingPdfModal
        visible={generatePDFPreview}
        onClose={() => setGeneratePDFPreview(false)}
        progress={generateLoading}
      />
    </div>
  );
};

export default AIRecommendation;
